import { gql, useMutation, useQuery } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Button } from "@mui/material";
import { stripTypename } from "@src/components/apollo/utils";
import DeleteDeviceDialog from "@src/components/dialogs/deleteDialog/DeleteDeviceDialog";
// eslint-disable-next-line @comet/no-private-sibling-import
import { StyledTypography } from "@src/components/dialogs/Dialog.sc";
import { InputField, ShowField } from "@src/components/fields";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import Form from "@src/components/Form";
import { CameraTypeSelect } from "@src/components/select/CameraTypeSelect";
import { getCountryName } from "@src/components/select/CountrySelect";
import { basicMonthOptions, premiumMonthOptions } from "@src/components/select/options";
// eslint-disable-next-line @comet/no-private-sibling-import
import { cameraFragment } from "@src/domain/devices/edit/fragments.gql";
import {
    type GQLCameraAdminQuery,
    GQLCameraStatus,
    type GQLLastReceivedLogAdminQuery,
    type GQLLastReceivedLogAdminQueryVariables,
    type GQLUpdateCameraAdminMutation,
    type GQLUpdateCameraAdminMutationVariables,
    type GQLUpdateCameraInput,
} from "@src/graphql.generated";
import { type ReactElement, useState } from "react";
import { useFormState } from "react-final-form";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";

import { DeleteUserDeviceRelationDialog } from "./DeleteUserDeviceRelationDialog";
import { DeleteCodeButtonIcon, FormButtonsWrapper } from "./Form.sc";

export const getCameraStatusValue = (status: GQLCameraStatus, intl: IntlShape) => {
    if (status === GQLCameraStatus.Active) {
        return intl.formatMessage({
            id: "reviermanager.camera.table.status.tooltip.activated",
            defaultMessage: "Aktiviert",
        });
    }
    if (status === GQLCameraStatus.Sleep) {
        return intl.formatMessage({
            id: "reviermanager.camera.table.status.tooltip.notActivated",
            defaultMessage: "Nicht aktiviert",
        });
    }

    return intl.formatMessage({
        id: "reviermanager.camera.table.status.tooltip.defect",
        defaultMessage: "Defekt",
    });
};

export const updateCameraMutation = gql`
    mutation UpdateCameraAdmin($id: ID!, $input: UpdateCameraInput!) {
        updateCameraAdmin(id: $id, input: $input) {
            ...CameraFragment
        }
    }
    ${cameraFragment}
`;

const lastReceivedLogAdmin = gql`
    query LastReceivedLogAdmin($cameraId: ID!) {
        lastReceivedLogAdmin(cameraId: $cameraId) {
            createdAt
        }
    }
`;

interface FormButtonsProps {
    handleDialogOpen: (type: DialogType) => void;
}

const FormButtons = ({ handleDialogOpen }: FormButtonsProps): ReactElement => {
    const {
        pristine,
        hasValidationErrors,
        submitting,
        initialValues: { user },
    } = useFormState();

    return (
        <FormButtonsWrapper>
            {user ? (
                <Button onClick={() => handleDialogOpen("userCameraRelation")} startIcon={<DeleteCodeButtonIcon />}>
                    <FormattedMessage
                        id="reviermanager.camera.userRelation.delete"
                        defaultMessage="Verbindung zwischen Gerät und Benutzer auflösen?"
                    />
                </Button>
            ) : (
                <Button onClick={() => handleDialogOpen("camera")} startIcon={<DeleteCodeButtonIcon />}>
                    <FormattedMessage id="reviermanager.camera.delete.button" defaultMessage="Gerät löschen" />
                </Button>
            )}

            <Button color="primary" variant="contained" startIcon={<Save />} disabled={pristine || hasValidationErrors || submitting} type="submit">
                <FormattedMessage id="reviermanager.button.save" defaultMessage="Speichern" />
            </Button>
        </FormButtonsWrapper>
    );
};

function isRoamingSim(msisdn: string): boolean {
    return msisdn.startsWith("42");
}

type DialogType = "" | "camera" | "userCameraRelation";

const EditForm = ({ cameraAdmin }: GQLCameraAdminQuery): ReactElement => {
    const intl = useIntl();
    const { id, activatedAt, ...initialValues } = cameraAdmin;
    const initialActivationAt = cameraAdmin.initialActivationAt;
    const [updateMutation] = useMutation<GQLUpdateCameraAdminMutation, GQLUpdateCameraAdminMutationVariables>(updateCameraMutation, {
        refetchQueries: ["SimplifySMSCodesAdmin"],
    });
    const [dialogOpen, setDialogOpen] = useState<DialogType>("");

    const { data } = useQuery<GQLLastReceivedLogAdminQuery, GQLLastReceivedLogAdminQueryVariables>(lastReceivedLogAdmin, {
        variables: { cameraId: cameraAdmin.id },
    });

    function handleDialogOpen(type: DialogType) {
        setDialogOpen(type);
    }

    function handleDialogClose() {
        setDialogOpen("");
    }

    const submit = ({
        name,
        activationPin,
        country,
        hardwareInfo,
        cameraId,
        status,
        userId,
        type,
        basicLicenceInMonths,
        premiumLicenceInMonths,
    }: GQLUpdateCameraInput) => {
        return updateMutation({
            variables: {
                id,
                input: {
                    hardwareInfo: stripTypename(stripTypename(hardwareInfo)),
                    premiumLicenceInMonths,
                    basicLicenceInMonths,
                    activationPin,
                    cameraId,
                    country,
                    userId,
                    status,
                    name,
                    type,
                },
            },
        });
    };

    return (
        <>
            <DeleteDeviceDialog
                open={dialogOpen === "camera"}
                handleClose={handleDialogClose}
                cameraId={cameraAdmin.id}
                title={<FormattedMessage id="reviermanager.common.device.delete.title" defaultMessage="Gerät löschen" />}
                dialogBody={
                    <StyledTypography>
                        <FormattedMessage
                            id="reviermanager.common.device.delete"
                            defaultMessage="Möchten Sie das Gerät {deviceId} wirklich löschen?"
                            values={{ deviceId: cameraAdmin.cameraId }}
                        />
                    </StyledTypography>
                }
            />
            <DeleteUserDeviceRelationDialog
                open={dialogOpen === "userCameraRelation"}
                handleClose={handleDialogClose}
                cameraId={cameraAdmin.id}
                title={
                    <FormattedMessage
                        id="reviermanager.camera.userRelation.delete.dialog.title"
                        defaultMessage="Verbindung zwischen Gerät und Benutzer auflösen"
                    />
                }
                dialogBody={
                    <StyledTypography>
                        <FormattedMessage
                            id="reviermanager.camera.userRelation.delete.dialog.message"
                            defaultMessage="Möchten Sie die Verbindung zwischen {fullname} und dem Gerät {deviceId} wirklich auflösen?"
                            values={{
                                deviceId: cameraAdmin.cameraId,
                                fullname:
                                    cameraAdmin.user?.firstname && cameraAdmin.user?.lastname
                                        ? `${cameraAdmin.user?.firstname} ${cameraAdmin.user?.lastname}`
                                        : "",
                            }}
                        />
                    </StyledTypography>
                }
            />
            <Form
                mode="edit"
                onSubmit={submit}
                onAfterSubmit={() => {
                    // do nothing
                }}
                initialValues={initialValues}
                title={intl.formatMessage({ id: "reviermanager.devices.form.edit.title", defaultMessage: "Gerätedaten" })}
                customButtons={<FormButtons handleDialogOpen={handleDialogOpen} />}
            >
                <InputField name="hardwareInfo.imei" label={intl.formatMessage({ id: "reviermanager.camera.imei", defaultMessage: "IMEI" })} />
                <InputField name="hardwareInfo.sn" label={intl.formatMessage({ id: "reviermanager.camera.sn", defaultMessage: "SN" })} />
                <InputField name="hardwareInfo.msisdn" label={intl.formatMessage({ id: "reviermanager.camera.msisdn", defaultMessage: "MSISDN" })} />
                <ShowField
                    label={intl.formatMessage({ id: "reviermanager.camera.roaming.sim", defaultMessage: "MKL SIM-Karte" })}
                    type="switch"
                    value={isRoamingSim(initialValues.hardwareInfo.msisdn)}
                />
                <InputField name="name" label={intl.formatMessage({ id: "reviermanager.camera.name", defaultMessage: "Name" })} />
                <InputField
                    name="cameraId"
                    type="number"
                    label={intl.formatMessage({ id: "reviermanager.camera.cameraId", defaultMessage: "Kamera-ID" })}
                />
                <InputField
                    type="number"
                    name="activationPin"
                    label={intl.formatMessage({ id: "reviermanager.camera.activationPin", defaultMessage: "Aktivierungs-PIN" })}
                />
                <StaticSelectField
                    name="type"
                    component={CameraTypeSelect}
                    label={intl.formatMessage({ id: "reviermanager.camera.type", defaultMessage: "Typ" })}
                />
                <ShowField
                    value={cameraAdmin.hardwareInfo.firmware}
                    label={intl.formatMessage({ id: "reviermanager.camera.hardwareinfo.firmware", defaultMessage: "Firmware" })}
                />
                <ShowField
                    value={getCountryName(cameraAdmin.country)}
                    label={intl.formatMessage({ id: "reviermanager.camera.country", defaultMessage: "Land" })}
                />
                <ShowField
                    type="date"
                    value={activatedAt}
                    label={intl.formatMessage({ id: "reviermanager.camera.activatedAt", defaultMessage: "Aktiviert am" })}
                />
                <ShowField
                    type="date"
                    value={initialActivationAt}
                    label={intl.formatMessage({ id: "reviermanager.camera.initialActivationAt", defaultMessage: "Erstaktivierung" })}
                />
                <StaticSelectField
                    name="basicLicenceInMonths"
                    label={intl.formatMessage({ id: "reviermanager.starter.licence.basic", defaultMessage: "Basislizenz" })}
                    options={basicMonthOptions}
                    isDisabled={!!initialActivationAt}
                    searchable={false}
                />
                <StaticSelectField
                    name="premiumLicenceInMonths"
                    label={intl.formatMessage({ id: "reviermanager.starter.licence.premium", defaultMessage: "Premiumlizenz" })}
                    options={premiumMonthOptions}
                    isDisabled={!!initialActivationAt}
                    searchable={false}
                />
                <ShowField
                    label={intl.formatMessage({ id: "reviermanager.camera.user.email", defaultMessage: "Benutzer E-Mail" })}
                    value={cameraAdmin.user?.email}
                />
                <ShowField
                    type="date"
                    value={getLatestActivity(cameraAdmin.lastReceivedImageAt, data?.lastReceivedLogAdmin?.createdAt)}
                    label={intl.formatMessage({ id: "reviermanager.camera.lastActivity", defaultMessage: "Letzte Aktivität" })}
                />
            </Form>
        </>
    );
};
export default EditForm;

function getLatestActivity(lastReceivedImageAt: string | undefined, lastReceivedSMSAt: string | undefined) {
    const lastImage = lastReceivedImageAt ? new Date(lastReceivedImageAt) : null;
    const lastSMS = lastReceivedSMSAt ? new Date(lastReceivedSMSAt) : null;

    if (lastImage && lastSMS) {
        return lastImage > lastSMS ? lastImage : lastSMS;
    }

    return lastImage ? lastImage : lastSMS ? lastSMS : null;
}
